import { AxiosResponse } from 'axios'
import { http } from '@/core/realisations/Http.realisation'

import {
    VacanciesGetListResponseT,
    VacanciesGetFeedBacksListResponseT,
    VacanciesGetCategoriesListResponseT,
    VacanciesGetRegionsListResponseT,
    VacanciesGetExperienceListResponseT,
    VacanciesCreateExperiencePayloadT,
    VacanciesCreateExperienceResponseT,
    VacanciesCreateRegionPayloadT,
    VacanciesCreateRegionResponseT,
    VacanciesUpdateExperienceResponseT,
    VacanciesCreateCategoryPayloadT,
    VacanciesCreateCategoryResponseT,
    VacanciesUpdateRegionResponseT,
    VacanciesUpdateCategoryResponseT,
    VacanciesCreateVacancyPayloadT,
    VacanciesUpdateVacancyResponseT,
    VacanciesCreateVacancyResponseT,
    VacanciesGetVacancyResponseT,
    VacanciesUpdateVacancyStatusResponseT,
    VacanciesGetVacancyStatusesResponseT,
} from '@/core/types/vacancies.types'

enum ROUTES {
    // get
    'GET_LIST' = '/vacancy/api/vacancy',
    'GET_VACANCY' = '/vacancy/api/vacancy',
    'GET_VACANCY_STATUSES' = '/vacancy/api/vacancy-status',
    'GET_FEEDBACKS_LIST' = '/vacancy/api/feedbacks',
    'GET_CATEGORIES_LIST' = '/vacancy/api/category',
    'GET_REGIONS_LIST' = '/vacancy/api/region',
    'GET_EXPERIENCE_LIST' = '/vacancy/api/experience',
    // post
    'CREATE_EXPERIENCE' = '/vacancy/api/experience',
    'CREATE_REGION' = '/vacancy/api/region',
    'CREATE_CATEGORY' = '/vacancy/api/category',
    'CREATE_VACANCY' = '/vacancy/api/vacancy',
    'PUBLISCH_VACANCY' = '/vacancy/api/vacancy',
    'ARCHIVE_VACANCY' = '/vacancy/api/vacancy',
    'ARCHIVE_FEEDBACK' = '/vacancy/api/move-to-archive',
    'UNARCHIVE_FEEDBACK' = '/vacancy/api/restore-from-archive',
    'SET_FEEDBACK_STATUS' = '/vacancy/api/feedbacks/set-status',
    // put
    'UPDATE_EXPERIENCE' = '/vacancy/api/experience',
    'UPDATE_REGION' = '/vacancy/api/region',
    'UPDATE_CATEGORY' = '/vacancy/api/category',
    'UPDATE_VACANCY' = '/vacancy/api/vacancy',
    'UPDATE_VACANCY_STATUS' = '/vacancy/api/vacancy/set-status/',
    // delete
    'DELETE_EXPERIENCE' = '/vacancy/api/experience',
    'DELETE_REGION' = '/vacancy/api/region',
    'DELETE_CATEGORY' = '/vacancy/api/category',
    'DELETE_VACANCY' = '/vacancy/api/vacancy',
}

export class VacanciesRepository {
    // get methods
    getList(page: number): Promise<AxiosResponse<VacanciesGetListResponseT>> {
        const params = {
            page,
        }

        return http.get(ROUTES.GET_LIST, { params })
    }

    getVacancyStatuses(): Promise<AxiosResponse<VacanciesGetVacancyStatusesResponseT>> {
        return http.get(ROUTES.GET_VACANCY_STATUSES)
    }

    getVacancy(vacancy_id: number): Promise<AxiosResponse<VacanciesGetVacancyResponseT>> {
        return http.get(`${ROUTES.GET_VACANCY}/${vacancy_id}`)
    }

    getFeedBacksList(page: number): Promise<AxiosResponse<VacanciesGetFeedBacksListResponseT>> {
        const params = {
            page,
        }

        return http.get(ROUTES.GET_FEEDBACKS_LIST, { params })
    }

    getCategoriesList(page: number): Promise<AxiosResponse<VacanciesGetCategoriesListResponseT>> {
        const params = {
            page,
        }

        return http.get(ROUTES.GET_CATEGORIES_LIST, { params })
    }

    getRegionsList(page: number): Promise<AxiosResponse<VacanciesGetRegionsListResponseT>> {
        const params = {
            page,
        }

        return http.get(ROUTES.GET_REGIONS_LIST, { params })
    }

    getExperienceList(page: number): Promise<AxiosResponse<VacanciesGetExperienceListResponseT>> {
        const params = {
            page,
        }

        return http.get(ROUTES.GET_EXPERIENCE_LIST, { params })
    }

    // post methods
    createExperience(
        experience_payload: VacanciesCreateExperiencePayloadT
    ): Promise<AxiosResponse<VacanciesCreateExperienceResponseT>> {
        return http.post(ROUTES.CREATE_EXPERIENCE, experience_payload)
    }

    createRegion(
        region_payload: VacanciesCreateRegionPayloadT
    ): Promise<AxiosResponse<VacanciesCreateRegionResponseT>> {
        return http.post(ROUTES.CREATE_REGION, region_payload)
    }

    createCategory(
        category_payload: VacanciesCreateCategoryPayloadT
    ): Promise<AxiosResponse<VacanciesCreateCategoryResponseT>> {
        return http.post(ROUTES.CREATE_CATEGORY, category_payload)
    }

    createVacancy(
        vacancy_payload: VacanciesCreateVacancyPayloadT
    ): Promise<AxiosResponse<VacanciesCreateVacancyResponseT>> {
        return http.post(ROUTES.CREATE_VACANCY, vacancy_payload)
    }

    publishVacancy(vacancy_id: number): Promise<AxiosResponse<void>> {
        return http.put(`${ROUTES.PUBLISCH_VACANCY}/${vacancy_id}/publish`, undefined)
    }

    archiveVacancy(vacancy_id: number): Promise<AxiosResponse<void>> {
        return http.put(`${ROUTES.ARCHIVE_VACANCY}/${vacancy_id}/archive`, undefined)
    }

    // put methods
    updateExperience(
        experience_id: number,
        experience_payload: VacanciesCreateExperiencePayloadT
    ): Promise<AxiosResponse<VacanciesUpdateExperienceResponseT>> {
        return http.put(`${ROUTES.UPDATE_EXPERIENCE}/${experience_id}`, experience_payload)
    }

    updateRegion(
        region_id: number,
        region_payload: VacanciesCreateRegionPayloadT
    ): Promise<AxiosResponse<VacanciesUpdateRegionResponseT>> {
        return http.put(`${ROUTES.UPDATE_REGION}/${region_id}`, region_payload)
    }

    updateCategory(
        category_id: number,
        category_payload: VacanciesCreateCategoryPayloadT
    ): Promise<AxiosResponse<VacanciesUpdateCategoryResponseT>> {
        return http.put(`${ROUTES.UPDATE_CATEGORY}/${category_id}`, category_payload)
    }

    updateVacancy(
        vacancy_id: number,
        vacancy_payload: VacanciesCreateVacancyPayloadT
    ): Promise<AxiosResponse<VacanciesUpdateVacancyResponseT>> {
        return http.put(`${ROUTES.UPDATE_VACANCY}/${vacancy_id}`, vacancy_payload)
    }

    updateVacancyStatus(
        vacancy_id: number,
        status_id: number
    ): Promise<AxiosResponse<VacanciesUpdateVacancyStatusResponseT>> {
        return http.put(`${ROUTES.UPDATE_VACANCY_STATUS}/${vacancy_id}`, { statusId: status_id })
    }

    archiveFeedback(feedback_id: number): Promise<AxiosResponse<void>> {
        return http.put(`${ROUTES.ARCHIVE_FEEDBACK}/${feedback_id}`, undefined)
    }

    unarchiveFeedback(feedback_id: number): Promise<AxiosResponse<void>> {
        return http.put(`${ROUTES.UNARCHIVE_FEEDBACK}/${feedback_id}`, undefined)
    }

    setFeedbackStatus(feedback_id: number, status_id: number): Promise<AxiosResponse<void>> {
        return http.put(`${ROUTES.SET_FEEDBACK_STATUS}/${feedback_id}`, { status_id })
    }

    // delete methods
    deleteExperience(experience_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`${ROUTES.DELETE_EXPERIENCE}/${experience_id}`)
    }

    deleteRegion(region_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`${ROUTES.DELETE_REGION}/${region_id}`)
    }

    deleteCategory(category_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`${ROUTES.DELETE_CATEGORY}/${category_id}`)
    }

    deleteVacancy(vacancy_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`${ROUTES.DELETE_VACANCY}/${vacancy_id}`)
    }
}
